import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

/**
 * The four plate roles. Web's `ComponentRole` shares these exact string values, so its
 * members are assignable to a PlateRoleChip `role` without importing across packages.
 */
export enum PlateRole {
  Protein = 'protein',
  Starch = 'starch',
  Veggie = 'veggie',
  Sauce = 'sauce'
}

/** Any enum/string carrying a plate-role value — PlateRole.* or web's ComponentRole.*. */
type RoleValue = `${PlateRole}`
type PlateRoleShape = 'pill' | 'tag' | 'dot'

// Full, literal class strings per role: Tailwind's content scan only sees complete
// class names, so these can't be built by interpolation (`text-role-${role}`).
const ROLE_TEXT: Record<RoleValue, string> = {
  protein: 'text-role-protein',
  starch: 'text-role-starch',
  veggie: 'text-role-veggie',
  sauce: 'text-role-sauce'
}
const ROLE_SOFT_BG: Record<RoleValue, string> = {
  protein: 'bg-role-protein-bg',
  starch: 'bg-role-starch-bg',
  veggie: 'bg-role-veggie-bg',
  sauce: 'bg-role-sauce-bg'
}
const ROLE_DOT_BG: Record<RoleValue, string> = {
  protein: 'bg-role-protein',
  starch: 'bg-role-starch',
  veggie: 'bg-role-veggie',
  sauce: 'bg-role-sauce'
}
// Mobile halo ring behind the collapsed dot — role-tinted, only at the collapsible breakpoint.
const ROLE_HALO: Record<RoleValue, string> = {
  protein: 'max-sm:shadow-[0_0_0_3px_var(--role-protein-bg)]',
  starch: 'max-sm:shadow-[0_0_0_3px_var(--role-starch-bg)]',
  veggie: 'max-sm:shadow-[0_0_0_3px_var(--role-veggie-bg)]',
  sauce: 'max-sm:shadow-[0_0_0_3px_var(--role-sauce-bg)]'
}

interface PlateRoleChipProps {
  /** Which plate role — drives the color. Pass `PlateRole.*` or web's `ComponentRole.*`. */
  role: RoleValue
  /** `pill` = soft-bg + label; `tag` = label only, no bg; `dot` = bare colored dot. Default `pill`. */
  shape?: PlateRoleShape
  /** pill/tag: show the leading dot. Default true. */
  dot?: boolean
  /** pill only: collapse to a dot-with-halo on mobile (`max-sm`) — e.g. cooking-mode focus tags. */
  collapsible?: boolean
  /** pill/tag: the label content (the caller owns any suffix, e.g. "Protein · Golden"). */
  children?: ReactNode
  /** dot only: accessible title, since the dot has no visible label. */
  label?: string
  /** pill/tag: tooltip on the chip — important for a `collapsible` pill, whose label hides on mobile. */
  title?: string
  className?: string
}

/** A bare role-colored dot. */
function RoleDot({ role, className }: { role: RoleValue; className?: string }) {
  return <span className={cn('shrink-0 rounded-full', ROLE_DOT_BG[role], className)} />
}

export function PlateRoleChip({
  role,
  shape = 'pill',
  dot = true,
  collapsible = false,
  children,
  label,
  title,
  className
}: PlateRoleChipProps) {
  if (shape === 'dot') {
    return <span className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', ROLE_DOT_BG[role], className)} title={label} />
  }

  if (shape === 'tag') {
    return (
      <span
        title={title}
        className={cn(
          'inline-flex items-center gap-1 text-2xs font-bold uppercase leading-none tracking-[0.05em]',
          ROLE_TEXT[role],
          className
        )}>
        {dot && <RoleDot role={role} className="h-1.5 w-1.5" />}
        {children}
      </span>
    )
  }

  // pill
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold uppercase leading-none tracking-[0.06em]',
        ROLE_SOFT_BG[role],
        ROLE_TEXT[role],
        collapsible && 'max-sm:gap-0 max-sm:bg-transparent max-sm:p-0',
        className
      )}>
      {dot && <RoleDot role={role} className={cn('h-2 w-2', collapsible && cn('max-sm:h-[11px] max-sm:w-[11px]', ROLE_HALO[role]))} />}
      {children && (collapsible ? <span className="max-sm:hidden">{children}</span> : children)}
    </span>
  )
}
